export const words = (value: string | null | undefined): string =>
  value ? value.replace(/[._]/g, " ") : "Unknown";

const inZone = (
  value: string,
  timezone: string,
  options: Intl.DateTimeFormatOptions,
  utcOptions: Intl.DateTimeFormatOptions = options,
): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Time unavailable";
  try {
    return new Intl.DateTimeFormat(undefined, {
      ...options,
      timeZone: timezone,
    }).format(date);
  } catch {
    return (
      new Intl.DateTimeFormat(undefined, {
        ...utcOptions,
        timeZone: "UTC",
      }).format(date) + " UTC"
    );
  }
};

export const clinicTime = (value: string, timezone: string): string =>
  inZone(
    value,
    timezone,
    {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      year: "numeric",
      timeZoneName: "short",
    },
    { dateStyle: "medium", timeStyle: "short" },
  );

/** To the second, for Jev calls a couple of seconds apart. */
export const clinicClock = (value: string, timezone: string): string =>
  inZone(value, timezone, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });

export const duration = (milliseconds: number | null): string => {
  if (milliseconds === null || !Number.isFinite(milliseconds))
    return "Not measured";
  return milliseconds < 1000
    ? `${Math.round(milliseconds)} ms`
    : `${(milliseconds / 1000).toFixed(1)} s`;
};

export const readableEvidence = (value: unknown): string => {
  if (value === null || value === undefined) return "Not recorded";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value, null, 2) ?? "Not recorded";
  } catch {
    return "Recorded evidence could not be displayed";
  }
};

export const percent = (value: number): string =>
  `${(value * 100).toFixed(1).replace(/\.0$/, "")}%`;

export const byLikelihood = (
  probabilities: Record<string, number>,
): [string, number][] =>
  Object.entries(probabilities).sort(([, a], [, b]) => b - a);

/** A rate without observations is "—", never 0%. */
export const rate = (numerator: number, denominator: number): string =>
  denominator > 0
    ? `${percent(numerator / denominator)} (${numerator}/${denominator})`
    : "—";
